import React, { useEffect, useRef, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/common/RichTextEditor";
import { SendLetterDialog, SentLog } from "@/pages/admin/sections/LetterSend";
import DOMPurify from "dompurify";
import { FileText, Plus, Settings, ArrowLeft, Copy, Trash2, Download, Eye, Lock, Unlock, Upload, Star, FileUp, RefreshCw, Send, ListChecks } from "lucide-react";

const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");
const A4 = { w: 210, h: 297 };
const todayStr = () => { const d = new Date(); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; };

// ---------------- Live A4 preview ----------------
const A4Preview = ({ bgUrl, margins, bodyHtml, letter }) => {
  const m = margins || { top: 42, right: 14, bottom: 31, left: 54 };
  const pct = (v, dim) => `${(v / dim) * 100}%`;
  return (
    <div data-testid="letter-a4-preview" className="relative mx-auto w-full overflow-hidden rounded border border-gmoaa-border-subtle bg-white shadow-sm"
      style={{ aspectRatio: `${A4.w}/${A4.h}`, backgroundImage: bgUrl ? `url(${mediaUrl(bgUrl)})` : "none", backgroundSize: "100% 100%", backgroundRepeat: "no-repeat" }}>
      <div className="absolute overflow-hidden border border-dashed border-slate-400 text-[7px] leading-snug text-slate-900"
        style={{ top: pct(m.top, A4.h), bottom: pct(m.bottom, A4.h), left: pct(m.left, A4.w), right: pct(m.right, A4.w) }}>
        <div className="flex justify-between"><span><b>Ref:</b> {letter?.ref_no}</span><span><b>Date:</b> {letter?.date}</span></div>
        {letter?.to && <div className="mt-1 whitespace-pre-line">To,<br />{letter.to}</div>}
        {letter?.subject && <div className="mt-1 font-bold">Subject: {letter.subject}</div>}
        <div className="mt-1 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-slate-400 [&_td]:p-0.5 [&_ul]:list-disc [&_ul]:pl-3 [&_ol]:list-decimal [&_ol]:pl-3"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bodyHtml || "") }} />
        {letter?.signatory && <div className="mt-3"><b>{letter.signatory}</b><br />{letter.signatory_role}</div>}
      </div>
    </div>
  );
};

// ---------------- Letterpad Settings ----------------
const LetterpadSettings = ({ onBack }) => {
  const [pads, setPads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => {
    adminApi.letterpads().then(setPads);
    adminApi.letterSettings().then(setSettings);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!name.trim()) { toast.error("Enter a name for this letterpad first"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be 10 MB or smaller"); return; }
    setBusy(true);
    try {
      const res = await adminApi.createLetterpad(file, name.trim());
      if (res.warning) toast.warning(res.warning); else toast.success("Letterpad uploaded");
      setName(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const saveMargins = async (pad) => {
    try { await adminApi.updateLetterpad(pad.id, { name: pad.name, margins: pad.margins }); toast.success("Saved"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  const activate = async (id) => { await adminApi.activateLetterpad(id); toast.success("Activated"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this letterpad?")) return; try { await adminApi.deleteLetterpad(id); toast.success("Deleted"); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Cannot delete"); } };

  const setPadField = (id, field, val) => setPads((p) => p.map((x) => x.id === id ? { ...x, [field]: val } : x));
  const setMargin = (id, key, val) => setPads((p) => p.map((x) => x.id === id ? { ...x, margins: { ...x.margins, [key]: Number(val) } } : x));

  const saveSettings = async () => { try { await adminApi.saveLetterSettings(settings); toast.success("Settings saved"); } catch { toast.error("Save failed"); } };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-gmoaa-green"><Settings size={20} /> Letterpad Settings</h3>
        <Button variant="ghost" onClick={onBack} data-testid="letters-back"><ArrowLeft size={16} className="mr-1" /> Back</Button>
      </div>

      <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-4">
        <p className="mb-2 text-sm font-medium text-gmoaa-text-dark">Upload a new letterpad (PDF, PNG or JPG · A4)</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]"><Label className="mb-1 block text-xs">Name</Label>
            <Input data-testid="letterpad-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 2026-28 Committee" /></div>
          <Button data-testid="letterpad-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">
            <Upload size={15} className="mr-1.5" /> {busy ? "Uploading…" : "Upload"}
          </Button>
          <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" onChange={upload} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {pads.map((pad) => (
          <div key={pad.id} data-testid={`letterpad-${pad.id}`} className={`rounded-xl border bg-white p-4 ${pad.is_active ? "border-gmoaa-green ring-1 ring-gmoaa-green" : "border-gmoaa-border-subtle"}`}>
            <div className="flex gap-3">
              <div className="relative w-28 shrink-0">
                <A4Preview bgUrl={pad.thumb_url || pad.file_url} margins={pad.margins} bodyHtml="<p style='color:#2563eb'>Body text area</p>" letter={{ ref_no: "GMOAA/…", date: "" }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Input value={pad.name} onChange={(e) => setPadField(pad.id, "name", e.target.value)} className="h-8 text-sm" />
                  {pad.is_active && <span className="flex items-center gap-1 rounded-full bg-gmoaa-green-light px-2 py-0.5 text-[10px] font-semibold text-gmoaa-green"><Star size={10} /> Active</span>}
                </div>
                <p className="mt-1 text-[11px] text-gmoaa-text-muted">{pad.file_type?.toUpperCase()} · {pad.a4_ok ? "A4" : "⚠ not A4"}</p>
                <div className="mt-2 grid grid-cols-4 gap-1.5">
                  {["top", "right", "bottom", "left"].map((k) => (
                    <div key={k}><Label className="block text-[10px] capitalize text-gmoaa-text-muted">{k} mm</Label>
                      <Input data-testid={`margin-${k}-${pad.id}`} type="number" value={pad.margins?.[k] ?? ""} onChange={(e) => setMargin(pad.id, k, e.target.value)} className="h-7 px-1.5 text-xs" /></div>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => saveMargins(pad)} data-testid={`save-margins-${pad.id}`}>Save</Button>
                  {!pad.is_active && <Button size="sm" className="h-7 bg-gmoaa-green text-xs text-white hover:bg-gmoaa-green-hover" onClick={() => activate(pad.id)} data-testid={`activate-${pad.id}`}>Set active</Button>}
                  {!pad.is_active && <Button size="sm" variant="ghost" className="h-7 text-xs text-gmoaa-red" onClick={() => del(pad.id)} data-testid={`delete-pad-${pad.id}`}><Trash2 size={13} /></Button>}
                </div>
              </div>
            </div>
          </div>
        ))}
        {pads.length === 0 && <p className="text-sm text-gmoaa-text-muted">No letterpads yet. Upload one above.</p>}
      </div>

      {settings && (
        <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-4">
          <p className="mb-3 text-sm font-medium text-gmoaa-text-dark">Fonts &amp; defaults</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div><Label className="block text-xs">Base size (pt)</Label><Input type="number" step="0.5" value={settings.fonts?.base_size ?? 11.5} onChange={(e) => setSettings((s) => ({ ...s, fonts: { ...s.fonts, base_size: Number(e.target.value) } }))} /></div>
            <div><Label className="block text-xs">Line height</Label><Input type="number" step="0.1" value={settings.fonts?.line_height ?? 1.5} onChange={(e) => setSettings((s) => ({ ...s, fonts: { ...s.fonts, line_height: Number(e.target.value) } }))} /></div>
            <div><Label className="block text-xs">Default signatory</Label><Input value={settings.default_signatory || ""} onChange={(e) => setSettings((s) => ({ ...s, default_signatory: e.target.value }))} /></div>
            <div><Label className="block text-xs">Default designation</Label><Input value={settings.default_signatory_role || ""} onChange={(e) => setSettings((s) => ({ ...s, default_signatory_role: e.target.value }))} /></div>
          </div>
          <Button className="mt-3 bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" onClick={saveSettings} data-testid="save-letter-settings">Save defaults</Button>
        </div>
      )}
    </div>
  );
};

// ---------------- Letter Editor ----------------
const LetterEditor = ({ letterId, onBack }) => {
  const [letter, setLetter] = useState(null);
  const [body, setBody] = useState("");
  const [pads, setPads] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const editorRef = useRef(null);
  const docxRef = useRef(null);
  const [showSend, setShowSend] = useState(false);
  const [showLog, setShowLog] = useState(false);

  useEffect(() => {
    adminApi.letterpads().then(setPads);
    adminApi.listCommittees().then(setCommittees).catch(() => {});
    if (letterId) {
      adminApi.letter(letterId).then((l) => { setLetter(l); setBody(l.body_html || ""); editorRef.current?.setHTML(l.body_html || ""); });
    } else {
      adminApi.nextRef().then((r) => setLetter({ ref_no: r.ref_no, date: todayStr(), to: "", subject: "", signatory: "", signatory_role: "", copy_to: "", letterpad_id: "", status: "draft" }));
    }
  }, [letterId]);

  const set = (field) => (e) => setLetter((l) => ({ ...l, [field]: e.target.value }));
  const issued = letter?.status === "issued";
  const activePad = pads.find((p) => p.is_active);
  const curPad = pads.find((p) => p.id === (letter?.letterpad_id)) || activePad;
  const sigOptions = committees.flatMap((c) => (c.members || []).map((m) => m.name_en)).filter(Boolean);

  const save = async () => {
    const payload = { ...letter, body_html: editorRef.current?.getHTML() ?? body };
    setBusy(true);
    try {
      if (letter.id) { const r = await adminApi.updateLetter(letter.id, payload); setLetter(r); toast.success("Draft saved"); return r; }
      const r = await adminApi.createLetter(payload); setLetter(r); toast.success("Draft created"); return r;
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const ensureSaved = async () => (letter?.id ? letter : await save());

  const preview = async () => {
    const l = await ensureSaved(); if (!l?.id) return;
    try { const blob = await adminApi.letterPdfBlob(l.id, letter.letterpad_id); setPreviewUrl(URL.createObjectURL(blob)); }
    catch { toast.error("Preview failed"); }
  };

  const exportPdf = async () => {
    const l = await ensureSaved(); if (!l?.id) return;
    try {
      const blob = await adminApi.letterPdfBlob(l.id, letter.letterpad_id);
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `${(l.ref_no || "letter").replace(/\//g, "-")}-${(l.subject || "").slice(0, 40)}.pdf`; a.click();
    } catch { toast.error("Export failed"); }
  };

  const issue = async () => {
    const l = await ensureSaved(); if (!l?.id) return;
    if (!window.confirm("Mark as Issued? This locks editing and stores the final PDF.")) return;
    try { const r = await adminApi.issueLetter(l.id); setLetter(r); toast.success("Letter issued & locked"); } catch { toast.error("Issue failed"); }
  };
  const reopen = async () => { try { const r = await adminApi.reopenLetter(letter.id); setLetter(r); toast.success("Re-opened for editing"); } catch { toast.error("Re-open failed"); } };

  const importDocx = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setBusy(true);
    try { const r = await adminApi.importDocx(file); editorRef.current?.setHTML(r.html); setBody(r.html); toast.success("Imported from Word"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  if (!letter) return <p className="text-sm text-gmoaa-text-muted">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-gmoaa-green">
          <FileText size={20} /> {letter.id ? "Edit letter" : "New letter"}
          {issued && <span className="flex items-center gap-1 rounded-full bg-gmoaa-saffron-light px-2 py-0.5 text-xs font-semibold text-gmoaa-saffron-hover"><Lock size={12} /> Issued</span>}
        </h3>
        <Button variant="ghost" onClick={onBack} data-testid="letters-back"><ArrowLeft size={16} className="mr-1" /> Back to list</Button>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {/* Left: form + editor */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">Ref No</Label><Input data-testid="letter-ref" value={letter.ref_no || ""} onChange={set("ref_no")} disabled={issued} /></div>
            <div><Label className="mb-1 block text-xs">Date (DD/MM/YYYY)</Label><Input data-testid="letter-date" value={letter.date || ""} onChange={set("date")} disabled={issued} /></div>
          </div>
          <div><Label className="mb-1 block text-xs">To</Label><textarea data-testid="letter-to" value={letter.to || ""} onChange={set("to")} disabled={issued} rows={2} className="w-full rounded-lg border border-gmoaa-border-subtle px-3 py-2 text-sm" /></div>
          <div><Label className="mb-1 block text-xs">Subject</Label><Input data-testid="letter-subject" value={letter.subject || ""} onChange={set("subject")} disabled={issued} /></div>

          <div><Label className="mb-1 block text-xs">Body</Label>
            {!issued && (
              <div className="mb-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="text-xs" onClick={() => docxRef.current?.click()} data-testid="import-docx-btn"><FileUp size={13} className="mr-1" /> Import .docx</Button>
                <input ref={docxRef} type="file" accept=".docx" className="hidden" onChange={importDocx} />
                <span className="text-xs text-gmoaa-text-muted">Tip: paste directly from Word — formatting &amp; tables are kept.</span>
              </div>
            )}
            {issued
              ? <div className="rounded-xl border border-gmoaa-border-subtle bg-slate-50 p-4 text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }} />
              : <RichTextEditor ref={editorRef} initialHTML={body} onChange={setBody} />}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">Signatory</Label>
              <Input data-testid="letter-signatory" list="sig-list" value={letter.signatory || ""} onChange={set("signatory")} disabled={issued} />
              <datalist id="sig-list">{sigOptions.map((s, i) => <option key={i} value={s} />)}</datalist>
            </div>
            <div><Label className="mb-1 block text-xs">Designation</Label><Input value={letter.signatory_role || ""} onChange={set("signatory_role")} disabled={issued} /></div>
          </div>
          <div><Label className="mb-1 block text-xs">Copy To</Label><textarea value={letter.copy_to || ""} onChange={set("copy_to")} disabled={issued} rows={2} className="w-full rounded-lg border border-gmoaa-border-subtle px-3 py-2 text-sm" /></div>
          <div><Label className="mb-1 block text-xs">Letterpad</Label>
            <select data-testid="letter-letterpad" value={letter.letterpad_id || ""} onChange={set("letterpad_id")} disabled={issued} className="w-full rounded-lg border border-gmoaa-border-subtle px-3 py-2 text-sm">
              <option value="">Active letterpad {activePad ? `(${activePad.name})` : ""}</option>
              {pads.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_active ? " · active" : ""}</option>)}
            </select>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {!issued && <Button disabled={busy} onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" data-testid="letter-save">{busy ? "Saving…" : "Save draft"}</Button>}
            <Button variant="outline" onClick={preview} data-testid="letter-preview"><Eye size={15} className="mr-1" /> Preview PDF</Button>
            <Button variant="outline" onClick={exportPdf} data-testid="letter-export"><Download size={15} className="mr-1" /> Export PDF</Button>
            {!issued ? <Button onClick={issue} className="bg-gmoaa-saffron-hover text-white hover:opacity-90" data-testid="letter-issue"><Lock size={15} className="mr-1" /> Mark as Issued</Button>
              : <Button variant="outline" onClick={reopen} data-testid="letter-reopen"><Unlock size={15} className="mr-1" /> Re-open</Button>}
            {issued && <Button onClick={() => setShowSend(true)} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" data-testid="letter-send"><Send size={15} className="mr-1" /> Send</Button>}
            {issued && <Button variant="outline" onClick={() => setShowLog(true)} data-testid="letter-sentlog"><ListChecks size={15} className="mr-1" /> Sent log</Button>}
          </div>
        </div>

        {showSend && <SendLetterDialog letter={letter} onClose={() => setShowSend(false)} onSent={() => setShowLog(true)} />}
        {showLog && <SentLog letter={letter} onClose={() => setShowLog(false)} />}

        {/* Right: live preview */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gmoaa-text-muted">Live preview (approx. page 1)</p>
            {previewUrl && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={preview}><RefreshCw size={12} className="mr-1" /> Refresh exact PDF</Button>}
          </div>
          <div className="mx-auto max-w-md">
            <A4Preview bgUrl={curPad?.thumb_url || curPad?.file_url} margins={curPad?.margins} bodyHtml={body} letter={letter} />
          </div>
          {previewUrl && <iframe title="pdf" src={previewUrl} data-testid="letter-pdf-frame" className="h-[520px] w-full rounded border border-gmoaa-border-subtle" />}
        </div>
      </div>
    </div>
  );
};

// ---------------- Letters List ----------------
const LettersList = ({ onNew, onEdit, onSettings }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    adminApi.letters({ q: q || undefined, status: status !== "all" ? status : undefined }).then(setRows);
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const duplicate = async (id) => { await adminApi.duplicateLetter(id); toast.success("Duplicated"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this letter?")) return; await adminApi.deleteLetter(id); toast.success("Deleted"); load(); };
  const exportPdf = async (l) => { const blob = await adminApi.letterPdfBlob(l.id); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${(l.ref_no || "letter").replace(/\//g, "-")}.pdf`; a.click(); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-serif text-xl font-semibold text-gmoaa-green"><FileText size={20} /> Letters</h3>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onSettings} data-testid="letters-settings-btn"><Settings size={16} className="mr-1" /> Letterpad Settings</Button>
          <Button onClick={onNew} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover" data-testid="letters-new-btn"><Plus size={16} className="mr-1" /> New letter</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input data-testid="letters-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Ref / Subject / To / date" className="max-w-xs" />
        <select data-testid="letters-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-gmoaa-border-subtle px-3 text-sm">
          <option value="all">All</option><option value="draft">Draft</option><option value="issued">Issued</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gmoaa-border-subtle bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gmoaa-green-light text-left text-xs text-gmoaa-green">
            <tr><th className="p-2.5">Ref No</th><th className="p-2.5">Date</th><th className="p-2.5">Subject</th><th className="p-2.5">To</th><th className="p-2.5">Sent</th><th className="p-2.5">Status</th><th className="p-2.5 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} data-testid={`letter-row-${l.id}`} className="border-t border-gmoaa-border-subtle">
                <td className="p-2.5 font-medium text-gmoaa-text-dark">{l.ref_no}</td>
                <td className="p-2.5">{l.date}</td>
                <td className="max-w-[220px] truncate p-2.5">{l.subject}</td>
                <td className="max-w-[160px] truncate p-2.5 text-gmoaa-text-muted">{l.to}</td>
                <td className="p-2.5 text-xs text-gmoaa-text-muted" data-testid={`letter-sent-${l.id}`}>{l.delivery_total ? `Sent to ${l.sent_count} (read ${l.read_count})` : "—"}</td>
                <td className="p-2.5">
                  {l.status === "issued"
                    ? <span className="rounded-full bg-gmoaa-saffron-light px-2 py-0.5 text-xs font-semibold text-gmoaa-saffron-hover">Issued</span>
                    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Draft</span>}
                </td>
                <td className="p-2.5">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onEdit(l.id)} data-testid={`edit-letter-${l.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => exportPdf(l)} title="Download PDF"><Download size={14} /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => duplicate(l.id)} title="Duplicate"><Copy size={14} /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-gmoaa-red" onClick={() => del(l.id)} title="Delete"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-sm text-gmoaa-text-muted">No letters yet. Click "New letter" to create one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------- Parent ----------------
export const LettersSection = () => {
  const [view, setView] = useState("list");
  const [editId, setEditId] = useState(null);

  if (view === "settings") return <LetterpadSettings onBack={() => setView("list")} />;
  if (view === "editor") return <LetterEditor letterId={editId} onBack={() => { setEditId(null); setView("list"); }} />;
  return <LettersList onNew={() => { setEditId(null); setView("editor"); }} onEdit={(id) => { setEditId(id); setView("editor"); }} onSettings={() => setView("settings")} />;
};
